import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from "react";
import { useLocation, useNavigate } from "@tanstack/react-router";
import { AgentChatProvider } from "../chat/agentChatContext";
import { useAgentBudget, type useAgentBudget as UseAgentBudget } from "../chat/useAgentBudget";
import { createNavigateTool } from "../context/navigateTool";
import { registerScreenTools } from "../context/screenTools";
import { describeVoiceState, isVoiceInputSupported } from "@/shared/hooks/useVoiceInput";
import {
  useAgentVoiceDialog,
  type AgentVoiceDialog,
  type AgentVoiceDialogEnv,
} from "./useAgentVoiceDialog";
import {
  type VoiceLaunchAction,
  type VoiceLaunchActivity,
  type VoiceLaunchCommand,
} from "./voiceLaunch";

type AgentBudgetState = ReturnType<typeof UseAgentBudget>;

function initialVoiceActivity(): VoiceLaunchActivity {
  const supported = isVoiceInputSupported();
  return {
    listening: false,
    greeting: false,
    voiceMode: false,
    paused: false,
    phase: "idle",
    disabled: !supported,
    tooltip: describeVoiceState({ isSupported: supported, listening: false, error: null }),
  };
}

export interface AgentSession {
  voiceDialog: AgentVoiceDialog;
  budget: AgentBudgetState;
  activity: VoiceLaunchActivity;
  issue: (action: VoiceLaunchAction, extra?: { skipGreeting?: boolean }) => void;
  setExpanded: (expanded: boolean) => void;
  bindTranscript: (ref: RefObject<HTMLDivElement | null>, release: () => void) => void;
}

const AgentSessionContext = createContext<AgentSession | null>(null);

/** Shared voice + chat session, or null when this tree has no provider. */
export function useOptionalAgentSession(): AgentSession | null {
  return useContext(AgentSessionContext);
}

/**
 * One chat and one voice-dialog machine for the drawer, the expand dialog, and
 * `/ai-assist`. Surfaces read this instead of creating a second recognizer.
 */
export function AgentSessionProvider({
  drawerOpen,
  onOpenDrawer,
  children,
}: Readonly<{
  drawerOpen: boolean;
  onOpenDrawer: () => void;
  children: ReactNode;
}>) {
  return (
    <AgentChatProvider>
      <AgentVoiceSessionHost drawerOpen={drawerOpen} onOpenDrawer={onOpenDrawer}>
        {children}
      </AgentVoiceSessionHost>
    </AgentChatProvider>
  );
}

function AgentVoiceSessionHost({
  drawerOpen,
  onOpenDrawer,
  children,
}: Readonly<{
  drawerOpen: boolean;
  onOpenDrawer: () => void;
  children: ReactNode;
}>) {
  const location = useLocation();
  const navigate = useNavigate();
  const aiAssistPage = location.pathname.startsWith("/ai-assist");
  const [expanded, setExpanded] = useState(false);

  // navigate_to is registered here, on the always-mounted session host, so it
  // survives surface changes: a voice conversation that continues on the FAB
  // after the drawer closes (or after leaving /ai-assist) keeps the tool even
  // though the drawer and workspace that render it have unmounted.
  useEffect(() => registerScreenTools([createNavigateTool((to) => navigate({ to }))]), [navigate]);
  const [voiceCommand, setVoiceCommand] = useState<VoiceLaunchCommand | null>(null);
  const [activity, setActivity] = useState<VoiceLaunchActivity>(initialVoiceActivity);
  // Refresh usage whenever a surface opens (drawer, expand dialog, or /ai-assist)
  // so the banner recovers after the daily reset instead of staying disabled
  // until reload. A constant `true` would only ever fetch once on mount.
  const budget = useAgentBudget(drawerOpen || expanded || aiAssistPage);
  const transcriptRef = useRef<HTMLDivElement | null>(null);
  const releaseStickRef = useRef<() => void>(() => undefined);

  const voiceDialog = useAgentVoiceDialog({
    open: drawerOpen || expanded || aiAssistPage,
    voiceCommand,
    onVoiceActivity: setActivity,
    onRevealVisual: onOpenDrawer,
    transcriptRef,
    releaseStick: () => {
      releaseStickRef.current();
    },
    budget,
  });

  const issue = useCallback((action: VoiceLaunchAction, extra?: { skipGreeting?: boolean }) => {
    setVoiceCommand((current) => ({
      id: (current?.id ?? 0) + 1,
      action,
      skipGreeting: extra?.skipGreeting,
    }));
  }, []);

  const bindTranscript = useCallback(
    (ref: RefObject<HTMLDivElement | null>, release: () => void) => {
      transcriptRef.current = ref.current;
      releaseStickRef.current = release;
    },
    [],
  );

  const session = useMemo<AgentSession>(
    () => ({
      voiceDialog,
      budget,
      activity,
      issue,
      setExpanded,
      bindTranscript,
    }),
    [voiceDialog, budget, activity, issue, bindTranscript],
  );

  return <AgentSessionContext.Provider value={session}>{children}</AgentSessionContext.Provider>;
}

/** Fingerprint of the chat turn so a local hook host rerenders with the transcript. */
export function voiceDialogTurnKey(
  chat: Readonly<{
    streaming: boolean;
    entries: ReadonlyArray<{ id: string; streaming: boolean; text: string }>;
  }>,
): string {
  return `${chat.streaming}:${chat.entries
    .map((entry) => `${entry.id}:${entry.streaming}:${entry.text}`)
    .join("|")}`;
}

/**
 * Use the session voice dialog when a provider is above, otherwise run a local
 * machine (unit tests and isolated surfaces).
 */
export function AgentVoiceDialogSource({
  env,
  turnKey,
  children,
}: Readonly<{
  env: AgentVoiceDialogEnv;
  /**
   * Host-computed chat fingerprint. Isolated tests mutate a shared chat mock
   * and rerender the host; this key keeps the local hook on that render.
   */
  turnKey?: string;
  children: (dialog: AgentVoiceDialog) => ReactNode;
}>) {
  const session = useOptionalAgentSession();
  if (session !== null) {
    return children(session.voiceDialog);
  }
  return (
    <LocalAgentVoiceDialog env={env} turnKey={turnKey}>
      {children}
    </LocalAgentVoiceDialog>
  );
}

function LocalAgentVoiceDialog({
  env,
  children,
}: Readonly<{
  env: AgentVoiceDialogEnv;
  /**
   * Accepted for API parity with {@link AgentVoiceDialogSource} but not read:
   * the fingerprint only matters while a session provider is above.
   */
  turnKey?: string;
  children: (dialog: AgentVoiceDialog) => ReactNode;
}>) {
  const dialog = useAgentVoiceDialog(env);
  return children(dialog);
}

/** Point the session visual-scroll handoff at the drawer transcript. */
export function useBindVoiceTranscript(
  ref: RefObject<HTMLDivElement | null>,
  release: () => void,
): void {
  const session = useOptionalAgentSession();
  useEffect(() => {
    if (session === null) {
      return undefined;
    }
    session.bindTranscript(ref, release);
    return () => {
      session.bindTranscript({ current: null }, () => undefined);
    };
  }, [session, ref, release]);
}
